import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDependencies } from '@servicetitan/react-ioc';
import { Flex, Grid, Link, Text } from '@servicetitan/anvil2';

import { MessageCard } from '../../message-card/message-card';
import { ChatStore } from '../../../stores/chat.store';
import { MessageType } from '../../../models/message-type.model';
import { scrollToBottom } from '../../../utils/scroll-to-bottom';
import { formatDate } from '../../../utils/format-date';
import { InvoiceItemModel, InvoiceListModel } from '../../../models/invoice.model';
import { formatCurrency } from '../../../utils/format-currency';

interface InvoiceListProps {
    data: InvoiceListModel;
}

export const InvoiceList: FC<InvoiceListProps> = ({ data }) => {
    const [showMore, setShowMore] = useState(false);

    const [chatStore] = useDependencies(ChatStore);

    const invoices = useMemo(() => {
        if (showMore) {
            return data.invoices;
        }
        return data.invoices.slice(0, 3);
    }, [showMore, data.invoices]);

    const handleShowMore = (invoice: InvoiceItemModel) => {
        chatStore.enqueueMessages([
            {
                isMine: false,
                type: MessageType.RichText,
                showAvatar: true,
                text: `Here is information about the <strong>Invoice #${invoice.invoice_id}</strong>:`,
            },
            {
                isMine: false,
                type: MessageType.InvoiceDetails,
                data: invoice,
            },
        ]);
    };

    const toggleShowAll = useCallback(() => {
        setShowMore(!showMore);

        if (!showMore) {
            scrollToBottom();
        }
    }, [setShowMore, showMore]);

    return (
        <div className="w-100">
            {invoices.map((invoice, index) => {
                return (
                    <MessageCard
                        key={invoice.invoice_id}
                        className={classNames(index > 0 && 'm-t-2')}
                        flexDirection="column"
                    >
                        <Text variant="headline" el="h3" size="small">
                            Invoice ID #{invoice.invoice_id}
                        </Text>

                        <div className="m-b-1">
                            <Text variant="eyebrow" className="m-b-half" size="small">
                                Invoice Date
                            </Text>
                            <Text className="m-b-1" size="small">
                                {formatDate(new Date(invoice.invoice_date))}
                            </Text>
                        </div>

                        <div className="m-b-1">
                            <Text variant="eyebrow" className="m-b-half" size="small">
                                Invoice Due Date
                            </Text>
                            <Text className="m-b-1" size="small">
                                {formatDate(new Date(invoice.invoice_due_date))}
                            </Text>
                        </div>

                        <Grid
                            templateColumns="1fr 1fr"
                            gap="2"
                            justifyContent="space-between"
                            alignItems="stretch"
                            className="m-b-1"
                        >
                            <Flex direction="column">
                                <Text variant="eyebrow" className="m-b-half" size="small">
                                    Paid Status
                                </Text>

                                {invoice.paid_status === 'overdue' ? (
                                    <Text className="m-b-1 c-red-600" size="small">
                                        Overdue
                                    </Text>
                                ) : invoice.paid_status === 'paid' ? (
                                    <Text className="m-b-1 c-green-500" size="small">
                                        Paid
                                    </Text>
                                ) : (
                                    <Text className="m-b-1" size="small">
                                        {invoice.paid_status}
                                    </Text>
                                )}
                            </Flex>

                            <Flex direction="column">
                                <Text variant="eyebrow" className="m-b-half" size="small">
                                    Invoice Total
                                </Text>
                                <Text className="m-b-1" size="small">
                                    {formatCurrency(invoice.invoice_total)}
                                </Text>
                            </Flex>
                        </Grid>

                        <Link quiet appearance="primary" onClick={() => handleShowMore(invoice)}>
                            View more info
                        </Link>
                    </MessageCard>
                );
            })}
            {data.invoices.length > 3 && (
                <Link quiet appearance="primary" className="m-t-1" onClick={toggleShowAll}>
                    {showMore ? 'View fewer invoices' : 'View full list'}
                </Link>
            )}
        </div>
    );
};
